import { useEffect, useState } from "react"
import { Calculator, Eye, FileText, Filter, Plus, Search, Send, Stethoscope } from "lucide-react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PageHeader } from "@/components/PageHeader"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { criteriosData } from "@/lib/criteriosData"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { getPublicErrorMessage } from "@/lib/errors"
import { getOficinaAtendimentoStatus } from "@/lib/oficinaAtendimento"
import { getLojaComercialStatus, getRiscoCompra } from "@/lib/lojaComercial"

const categoriaPorPrefixo: Record<string, string> = {
  frontal: "Frontal",
  traseira: "Traseira",
  latdir: "Lateral Direita",
  latesq: "Lateral Esquerda",
  motor: "Motor",
  painel: "Painel e Instrumentos",
  interior: "Interior",
  extras: "Detalhes Extras",
}

const resolverNomeCriterio = (criterioId: string) => {
  const id = criterioId.trim().toLowerCase()
  return criteriosData.find((c) => c.id === id)?.nome || criterioId
}

const resolverCategoriaCriterio = (criterioId: string) => {
  const id = criterioId.trim().toLowerCase()
  const local = criteriosData.find((c) => c.id === id)
  if (local?.categoria) return local.categoria
  return categoriaPorPrefixo[id.split("_")[0]] || ""
}

interface Avaliacao {
  id: string
  veiculo_id: string
  status: "pendente" | "ativa" | "concluida" | "cancelada"
  nota_final?: number
  objetivo_comercial?: string | null
  share_token?: string | null
  cliente_nome?: string | null
  cliente_telefone?: string | null
  cliente_assinado_em?: string | null
  atendimento_finalizado_em?: string | null
  created_at: string
  reparosCount?: number
  custoEstimado?: number
  veiculos: {
    placa: string
    marca: string
    modelo: string
    ano: number
    cor: string
  }
  proposta?: {
    id: string
    status: string
    modalidade?: string | null
    share_token?: string | null
    cliente_assinado_em?: string | null
    valor_fipe?: number | null
    custo_reparos?: number | null
    valor_ofertado?: number | null
    margem_lucro?: number | null
  } | null
  diagnosticos?: Array<{
    id: string
    status: string
    share_token: string | null
    cliente_ciente_em: string | null
    cliente_autorizou_em: string | null
  }>
}

const isStatusReprovado = (status: string | null | undefined, aprovado?: boolean | null) =>
  status ? status === "reprovado" : aprovado === false

const isMissingStatusColumnError = (error: unknown) => {
  const message = typeof error === "object" && error && "message" in error ? String(error.message) : ""
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : ""

  return code === "42703" || message.includes("status does not exist")
}

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  })

const buildStatusMap = (prontoParaDocumento: string) => ({
  pendente: {
    label: "Pendente",
    variant: "secondary" as const,
    className: "bg-warning/14 text-warning-foreground border-transparent",
    helper: "Aguardando avanço",
  },
  ativa: {
    label: "Em andamento",
    variant: "default" as const,
    className: "bg-primary/12 text-primary border-transparent",
    helper: "Inspeção em progresso",
  },
  concluida: {
    label: "Concluída",
    variant: "outline" as const,
    className: "bg-success/14 text-success border-transparent",
    helper: prontoParaDocumento,
  },
  cancelada: {
    label: "Cancelada",
    variant: "destructive" as const,
    className: "border-transparent",
    helper: "Fluxo encerrado",
  },
})

interface ActionLabels {
  iniciar: string
  continuar: string
  criar: string
  ver: string
}

const getNextActionLabel = (avaliacao: Avaliacao, labels: ActionLabels) => {
  if (avaliacao.status === "concluida") {
    return avaliacao.proposta ? labels.ver : labels.criar
  }

  if (avaliacao.status === "ativa") {
    return labels.continuar
  }

  if (avaliacao.status === "cancelada") {
    return "Fluxo encerrado"
  }

  return labels.iniciar
}

const Avaliacoes = () => {
  const vertical = useTipoOperacao()
  const mostrarFinanceiro = vertical.documentos.mostrarValoresFinanceiros
  const statusMap = buildStatusMap(vertical.labels.prontoParaDocumento)
  const actionLabels: ActionLabels = {
    iniciar: vertical.labels.iniciarAcao,
    continuar: vertical.labels.continuarAcao,
    criar: vertical.labels.criarDocumento,
    ver: vertical.labels.verDocumento,
  }
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("todos")
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([])
  const [loading, setLoading] = useState(true)
  const isMobile = useIsMobile()

  useEffect(() => {
    fetchAvaliacoes()
  }, [])

  const fetchAvaliacoes = async () => {
    try {
      const { data, error } = await supabase
        .from("avaliacoes")
        .select(`
          *,
          veiculos:veiculo_id (
            placa,
            marca,
            modelo,
            ano,
            cor
          )
        `)
        .order("created_at", { ascending: false })

      if (error) {
        console.error("Erro ao carregar avaliações:", error)
        toast.error(getPublicErrorMessage(error.message, "Não foi possível carregar as avaliações agora."))
        return
      }

      const avaliacoesData = (data || []) as Avaliacao[]
      const avaliacoesIds = avaliacoesData.map((avaliacao) => avaliacao.id)
      const propostasPorAvaliacao: Record<string, NonNullable<Avaliacao["proposta"]>> = {}

      if (avaliacoesIds.length > 0) {
        const { data: propostasData } = await supabase
          .from("propostas")
          .select("id, avaliacao_id, status, modalidade, share_token, cliente_assinado_em, valor_fipe, custo_reparos, valor_ofertado, margem_lucro")
          .in("avaliacao_id", avaliacoesIds)
          .order("updated_at", { ascending: false })

        propostasData?.forEach((proposta) => {
          if (!propostasPorAvaliacao[proposta.avaliacao_id]) {
            propostasPorAvaliacao[proposta.avaliacao_id] = proposta
          }
        })
      }

      const metricasPorAvaliacao: Record<string, { reparosCount: number; custoEstimado: number }> = {}
      const diagnosticosPorAvaliacao: Record<string, Avaliacao["diagnosticos"]> = {}

      if (!mostrarFinanceiro && avaliacoesIds.length > 0) {
        const { data: diagnosticosData, error: diagnosticosError } = await supabase
          .from("diagnosticos")
          .select("id, avaliacao_id, status, share_token, cliente_ciente_em, cliente_autorizou_em")
          .in("avaliacao_id", avaliacoesIds)
          .order("updated_at", { ascending: false })

        if (diagnosticosError) {
          console.error("Erro ao carregar diagnósticos das entradas:", diagnosticosError)
        } else {
          diagnosticosData?.forEach((diagnostico) => {
            if (!diagnostico.avaliacao_id) return
            diagnosticosPorAvaliacao[diagnostico.avaliacao_id] ??= []
            diagnosticosPorAvaliacao[diagnostico.avaliacao_id]?.push(diagnostico)
          })
        }
      }

      if (avaliacoesIds.length > 0) {
        let criteriosData: Array<{
          avaliacao_id: string
          custo_reparo: number | null
          status?: string | null
          aprovado?: boolean | null
        }> | null = null

        let criteriosError: { message?: string; code?: string } | null = null

        const criteriosRes = await supabase
          .from("avaliacoes_criterios")
          .select("avaliacao_id, custo_reparo, status, aprovado")
          .in("avaliacao_id", avaliacoesIds)

        criteriosData = criteriosRes.data
        criteriosError = criteriosRes.error

        if (criteriosError && isMissingStatusColumnError(criteriosError)) {
          const fallbackRes = await supabase
            .from("avaliacoes_criterios")
            .select("avaliacao_id, custo_reparo, aprovado")
            .in("avaliacao_id", avaliacoesIds)

          criteriosData = fallbackRes.data as typeof criteriosData
          criteriosError = fallbackRes.error
        }

        if (criteriosError) {
          console.error("Erro ao carregar métricas das avaliações:", criteriosError)
        } else {
          criteriosData?.forEach((criterio) => {
            if (!isStatusReprovado(criterio.status, criterio.aprovado)) return

            const atual = metricasPorAvaliacao[criterio.avaliacao_id] ?? {
              reparosCount: 0,
              custoEstimado: 0,
            }

            atual.reparosCount += 1
            atual.custoEstimado += criterio.custo_reparo || 0
            metricasPorAvaliacao[criterio.avaliacao_id] = atual
          })
        }
      }

      setAvaliacoes(
        avaliacoesData.map((avaliacao) => ({
          ...avaliacao,
          proposta: propostasPorAvaliacao[avaliacao.id] ?? null,
          diagnosticos: diagnosticosPorAvaliacao[avaliacao.id] ?? [],
          reparosCount: metricasPorAvaliacao[avaliacao.id]?.reparosCount ?? 0,
          custoEstimado: metricasPorAvaliacao[avaliacao.id]?.custoEstimado ?? 0,
        }))
      )
    } catch (error) {
      toast.error("Erro inesperado ao carregar avaliações")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  const getAtendimento = (avaliacao: Avaliacao) =>
    getOficinaAtendimentoStatus(avaliacao, avaliacao.diagnosticos ?? [])
  const getComercial = (avaliacao: Avaliacao) =>
    getLojaComercialStatus(avaliacao, avaliacao.proposta)

  const filteredAvaliacoes = avaliacoes.filter((avaliacao) => {
    const matchesSearch =
      avaliacao.veiculos?.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avaliacao.veiculos?.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avaliacao.veiculos?.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avaliacao.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      avaliacao.cliente_telefone?.replace(/\D/g, "").includes(searchTerm.replace(/\D/g, ""))

    const atendimento = getAtendimento(avaliacao)
    const comercial = getComercial(avaliacao)
    const matchesStatus = statusFilter === "todos" || (
      mostrarFinanceiro
        ? statusFilter === "em_avaliacao"
          ? ["veiculo_cadastrado", "avaliacao_iniciada"].includes(comercial.status)
          : statusFilter === "prontas"
            ? comercial.status === "avaliacao_concluida"
            : statusFilter === "propostas_enviadas"
              ? comercial.status === "proposta_enviada"
              : statusFilter === "aceitas"
                ? comercial.status === "proposta_aceita"
                : statusFilter === "consignados"
                  ? comercial.tipo === "consignacao"
                  : avaliacao.status === statusFilter
        : statusFilter === "em_entrada"
          ? ["entrada_criada"].includes(atendimento.status)
          : statusFilter === "termo_pendente"
            ? ["entrada_finalizada"].includes(atendimento.status)
            : statusFilter === "aguardando_cliente"
              ? ["termo_emitido", "diagnostico_enviado"].includes(atendimento.status)
              : statusFilter === "diagnostico_enviado"
                ? atendimento.status === "diagnostico_enviado"
                : statusFilter === "finalizados"
                  ? atendimento.status === "finalizado"
                  : avaliacao.status === statusFilter
    )
    return matchesSearch && matchesStatus
  })

  const stats = mostrarFinanceiro
    ? [
        { label: "Em avaliação", value: avaliacoes.filter(({ status }) => status !== "concluida" && status !== "cancelada").length, note: "Negócios em análise" },
        { label: "Prontas para proposta", value: avaliacoes.filter((avaliacao) => getComercial(avaliacao).status === "avaliacao_concluida").length, note: "Aguardando documento" },
        { label: "Propostas enviadas", value: avaliacoes.filter((avaliacao) => getComercial(avaliacao).status === "proposta_enviada").length, note: "Aguardando aceite" },
      ]
    : [
        { label: "Entradas abertas", value: avaliacoes.filter(({ status }) => status !== "concluida" && status !== "cancelada").length, note: "Atendimentos em andamento" },
        { label: "Aguardando cliente", value: avaliacoes.filter((avaliacao) => ["diagnostico_enviado", "termo_emitido"].includes(getAtendimento(avaliacao).status)).length, note: "Links enviados sem ciência" },
        { label: "Autorizados", value: avaliacoes.filter((avaliacao) => getAtendimento(avaliacao).status === "cliente_autorizou").length, note: "Diagnósticos autorizados" },
      ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={vertical.labels.menuPrincipal}
        description={vertical.labels.descricaoListagem}
      >
        <Button asChild>
          <Link to="/veiculos">
            <Plus className="mr-2 h-4 w-4" />
            Selecionar veículo
          </Link>
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <Card key={item.label}>
            <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">{item.label}</p>
              <p className="font-data mt-2 text-[2rem] font-semibold leading-none text-foreground sm:mt-3 sm:text-3xl">{item.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">Base ativa</p>
            <p className="font-data mt-2 text-[2rem] font-semibold leading-none text-foreground sm:mt-3 sm:text-3xl">{filteredAvaliacoes.length}</p>
            <p className="mt-1 text-xs text-muted-foreground">Registros na visão atual</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-3.5 sm:p-4 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground sm:top-3.5" />
              <Input
                placeholder={mostrarFinanceiro ? "Buscar por placa, marca ou modelo..." : "Buscar por placa, cliente, telefone ou modelo..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-10 pl-10 sm:h-11"
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] md:flex">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={isMobile ? "w-full" : "w-52"}>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  {mostrarFinanceiro ? (
                    <>
                      <SelectItem value="em_avaliacao">Em avaliação</SelectItem>
                      <SelectItem value="prontas">Prontas para proposta</SelectItem>
                      <SelectItem value="propostas_enviadas">Propostas enviadas</SelectItem>
                      <SelectItem value="aceitas">Aceitas</SelectItem>
                      <SelectItem value="consignados">Consignados</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="em_entrada">Em entrada</SelectItem>
                      <SelectItem value="termo_pendente">Termo pendente</SelectItem>
                      <SelectItem value="aguardando_cliente">Aguardando cliente</SelectItem>
                      <SelectItem value="diagnostico_enviado">Diagnóstico enviado</SelectItem>
                      <SelectItem value="finalizados">Finalizados</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              <Button variant="outline" size={isMobile ? "default" : "icon"} className={isMobile ? "w-full justify-start sm:w-auto" : undefined}>
                <Filter className="h-4 w-4" />
                {isMobile && "Filtrar"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border/70 px-4 pb-4 pt-4 sm:px-6 sm:pt-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <CardTitle className="text-[1.35rem] sm:text-[1.55rem]">{mostrarFinanceiro ? "Fila de avaliações" : "Entradas da oficina"}</CardTitle>
              <CardDescription>
                {mostrarFinanceiro
                  ? "Leitura operacional da base com foco em andamento, decisão e fechamento."
                  : "Acompanhe veículos recebidos, termos, diagnósticos enviados e próximas ações."}
              </CardDescription>
            </div>
            <div className="text-xs text-muted-foreground sm:text-sm">
              <span className="font-data font-semibold text-foreground">{filteredAvaliacoes.length}</span> registro{filteredAvaliacoes.length !== 1 ? "s" : ""} na visão atual
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-5 md:pt-6">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            </div>
          ) : filteredAvaliacoes.length === 0 ? (
            <div className="empty-state-panel flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-4 rounded-full bg-primary/10 p-6">
                <FileText className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold">
                {searchTerm || statusFilter !== "todos"
                  ? (mostrarFinanceiro ? "Nenhuma avaliação corresponde à visão atual" : "Nenhuma entrada corresponde à visão atual")
                  : "Sua operação ainda não começou por aqui"}
              </h3>
              <p className="mb-6 mt-2 max-w-md text-muted-foreground">
                {searchTerm || statusFilter !== "todos"
                  ? (mostrarFinanceiro
                    ? "Revise os filtros para recuperar veículos em análise ou abra uma nova avaliação para alimentar a fila."
                    : "Revise os filtros para recuperar entradas, termos ou diagnósticos enviados.")
                  : (mostrarFinanceiro
                    ? "Comece pela primeira avaliação para criar histórico, gerar fechamento com mais segurança e acelerar a negociação na loja."
                    : "Comece pela primeira entrada para documentar o veículo, emitir o termo e registrar a ciência do cliente.")}
              </p>
              <Button asChild size={isMobile ? "sm" : "default"}>
                <Link to="/veiculos">
                  <Plus className="mr-2 h-4 w-4" />
                  Selecionar veículo
                </Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[calc(var(--radius)+0.05rem)] border border-border/70">
              <div className="hidden grid-cols-[1.4fr_0.95fr_0.75fr_0.85fr_1.2fr] gap-4 border-b border-border/70 bg-secondary/65 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground lg:grid">
                <span>Veículo</span>
                <span>Status</span>
                <span>{mostrarFinanceiro ? "Tipo / risco" : "Cliente"}</span>
                <span>{mostrarFinanceiro ? "Oferta / valor" : "Diagnóstico"}</span>
                <span className="text-right">Ações</span>
              </div>
              <div className="divide-y divide-border/70 bg-white/72">
                {filteredAvaliacoes.map((avaliacao) => {
                  const meta = statusMap[avaliacao.status] ?? statusMap.pendente
                  const atendimento = getAtendimento(avaliacao)
                  const comercial = getComercial(avaliacao)
                  const risco = getRiscoCompra(avaliacao.proposta?.valor_fipe, avaliacao.proposta?.custo_reparos ?? avaliacao.custoEstimado)
                  const statusLabel = mostrarFinanceiro ? comercial.label : atendimento.label
                  const statusHelper = mostrarFinanceiro ? comercial.helper : atendimento.helper
                  const diagnosticoPrincipal = avaliacao.diagnosticos?.[0] ?? null
                  const actionHref = mostrarFinanceiro
                    ? `/avaliacao/${avaliacao.id}`
                    : atendimento.status === "entrada_criada"
                      ? `/avaliacao/${avaliacao.id}`
                      : atendimento.status === "entrada_finalizada" || atendimento.status === "termo_emitido"
                        ? `/proposta/${avaliacao.id}`
                        : `/veiculo/${avaliacao.veiculo_id}`
                  const actionLabel = mostrarFinanceiro ? "Abrir" : atendimento.proximaAcao
                  return (
                    <div key={avaliacao.id} className="transition-colors hover:bg-accent/35">
                      <div className="grid gap-4 px-4 py-4 lg:hidden">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-data text-base font-semibold tracking-[0.04em] text-foreground">
                              {avaliacao.veiculos?.placa || "Sem placa"}
                            </h3>
                            <Badge variant="outline" className="px-2 py-1 text-[10px]">
                              {avaliacao.veiculos?.ano}
                            </Badge>
                            <Badge variant={meta.variant} className={meta.className}>
                              {statusLabel}
                            </Badge>
                          </div>
                          <p className="mt-1 truncate text-sm text-foreground">
                            {avaliacao.veiculos?.marca} {avaliacao.veiculos?.modelo}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {statusHelper}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {avaliacao.veiculos?.ano} · {mostrarFinanceiro ? comercial.proximaAcao : atendimento.proximaAcao}
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 lg:flex lg:items-center">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                              {mostrarFinanceiro ? "Tipo / risco" : "Cliente"}
                            </p>
                            <p className="mt-1 font-data text-sm font-semibold text-foreground">
                              {mostrarFinanceiro ? (comercial.tipo === "consignacao" ? "Consignação" : "Compra") : (avaliacao.cliente_nome || "Não informado")}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {mostrarFinanceiro
                                ? risco.label
                                : (avaliacao.cliente_telefone || "Sem telefone")}
                            </p>
                          </div>

                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                              {mostrarFinanceiro ? "Oferta / valor" : "Diagnóstico"}
                            </p>
                            <p className="mt-1 font-data text-sm font-semibold text-foreground">
                              {mostrarFinanceiro
                                ? avaliacao.proposta?.valor_ofertado
                                  ? formatCurrency(avaliacao.proposta.valor_ofertado)
                                  : "Pendente"
                                : diagnosticoPrincipal
                                  ? diagnosticoPrincipal.status === "autorizado"
                                    ? "Autorizado"
                                    : diagnosticoPrincipal.status === "ciente"
                                      ? "Cliente ciente"
                                      : diagnosticoPrincipal.share_token
                                        ? "Enviado"
                                        : "Criado"
                                  : "Pendente"}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {mostrarFinanceiro && avaliacao.status === "concluida" && avaliacao.nota_final
                                ? `Nota ${avaliacao.nota_final.toFixed(1)}/10`
                                : mostrarFinanceiro
                                  ? avaliacao.proposta?.modalidade === "consignacao" ? "valor anunciado" : `${avaliacao.reparosCount ?? 0} reparo${(avaliacao.reparosCount ?? 0) !== 1 ? "s" : ""}`
                                  : `${avaliacao.diagnosticos?.length ?? 0} registro${(avaliacao.diagnosticos?.length ?? 0) !== 1 ? "s" : ""}`}
                            </p>
                          </div>
                        </div>

                        <div className={`grid gap-2 ${avaliacao.status === "concluida" ? "grid-cols-2 sm:grid-cols-2" : "grid-cols-1"}`}>
                          <Button variant="outline" size={isMobile ? "default" : "sm"} asChild className={isMobile ? "w-full justify-start" : undefined}>
                            <Link to={actionHref}>
                              <Eye className="mr-2 h-4 w-4" />
                              {actionLabel}
                            </Link>
                          </Button>
                          {mostrarFinanceiro && avaliacao.status === "concluida" && (
                            <Button
                              variant={avaliacao.proposta ? "outline" : "default"}
                              size={isMobile ? "default" : "sm"}
                              asChild
                              className={isMobile ? "w-full justify-start sm:justify-center" : undefined}
                            >
                              <Link to={`/proposta/${avaliacao.id}`}>
                              {mostrarFinanceiro ? <Calculator className="mr-2 h-4 w-4" /> : <FileText className="mr-2 h-4 w-4" />}
                              {avaliacao.proposta ? vertical.labels.documentoFinal : vertical.labels.criarDocumento}
                              </Link>
                            </Button>
                          )}
                          {!mostrarFinanceiro && avaliacao.status === "concluida" && (
                            <Button variant="outline" size={isMobile ? "default" : "sm"} asChild className={isMobile ? "w-full justify-start" : undefined}>
                              <Link to={diagnosticoPrincipal ? `/diagnostico/${diagnosticoPrincipal.id}` : `/diagnostico/novo?veiculo=${avaliacao.veiculo_id}&avaliacao=${avaliacao.id}`}>
                                {diagnosticoPrincipal?.share_token ? <Send className="mr-2 h-4 w-4" /> : <Stethoscope className="mr-2 h-4 w-4" />}
                                {diagnosticoPrincipal ? "Abrir diagnóstico" : "Criar diagnóstico"}
                              </Link>
                            </Button>
                          )}
                          {!mostrarFinanceiro && atendimento.status !== "entrada_criada" && (
                            <Button
                              variant="ghost"
                              size={isMobile ? "default" : "sm"}
                              asChild
                              className="col-span-2 w-full justify-start text-muted-foreground"
                            >
                              <Link to={`/avaliacao/${avaliacao.id}`}>
                                <FileText className="mr-2 h-4 w-4" />
                                Ver entrada
                              </Link>
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="hidden gap-4 px-5 py-4 lg:grid lg:grid-cols-[1.4fr_0.95fr_0.75fr_0.85fr_1.2fr]">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-data text-base font-semibold tracking-[0.04em] text-foreground">
                              {avaliacao.veiculos?.placa || "Sem placa"}
                            </h3>
                            <Badge variant="outline" className="px-2 py-1 text-[10px]">
                              {avaliacao.veiculos?.ano}
                            </Badge>
                          </div>
                          <p className="mt-1 truncate text-sm text-foreground">
                            {avaliacao.veiculos?.marca} {avaliacao.veiculos?.modelo}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {avaliacao.veiculos?.ano} · {mostrarFinanceiro ? comercial.proximaAcao : atendimento.proximaAcao}
                          </p>
                        </div>

                        <div className="min-w-0">
                          <Badge variant={meta.variant} className={meta.className}>
                            {statusLabel}
                          </Badge>
                          <p className="mt-1 text-xs text-muted-foreground">{statusHelper}</p>
                        </div>

                        <div className="flex items-center">
                          <div>
                            <p className="font-data text-sm font-semibold text-foreground">
                              {mostrarFinanceiro ? (comercial.tipo === "consignacao" ? "Consignação" : "Compra") : (avaliacao.cliente_nome || "Não informado")}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {mostrarFinanceiro
                                ? risco.label
                                : (avaliacao.cliente_telefone || "Sem telefone")}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div>
                            <p className="font-data text-sm font-semibold text-foreground">
                              {mostrarFinanceiro
                                ? avaliacao.proposta?.valor_ofertado
                                  ? formatCurrency(avaliacao.proposta.valor_ofertado)
                                  : "Pendente"
                                : diagnosticoPrincipal
                                  ? diagnosticoPrincipal.status === "autorizado"
                                    ? "Autorizado"
                                    : diagnosticoPrincipal.status === "ciente"
                                      ? "Cliente ciente"
                                      : diagnosticoPrincipal.share_token
                                        ? "Enviado"
                                        : "Criado"
                                  : "Pendente"}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {mostrarFinanceiro && avaliacao.status === "concluida" && avaliacao.nota_final
                                ? `Nota ${avaliacao.nota_final.toFixed(1)}/10`
                                : mostrarFinanceiro
                                  ? avaliacao.proposta?.modalidade === "consignacao" ? "valor anunciado" : `${avaliacao.reparosCount ?? 0} reparo${(avaliacao.reparosCount ?? 0) !== 1 ? "s" : ""}`
                                  : `${avaliacao.diagnosticos?.length ?? 0} registro${(avaliacao.diagnosticos?.length ?? 0) !== 1 ? "s" : ""}`}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col items-stretch gap-2 justify-center">
                          <Button variant="outline" size="sm" className="min-w-[172px] justify-center" asChild>
                            <Link to={actionHref}>
                              <Eye className="mr-2 h-4 w-4" />
                              {actionLabel}
                            </Link>
                          </Button>
                          {mostrarFinanceiro && avaliacao.status === "concluida" && (
                            <Button
                              variant={avaliacao.proposta ? "outline" : "default"}
                              size="sm"
                              className="min-w-[172px] justify-center"
                              asChild
                            >
                              <Link to={`/proposta/${avaliacao.id}`}>
                                {mostrarFinanceiro ? <Calculator className="mr-2 h-4 w-4" /> : <FileText className="mr-2 h-4 w-4" />}
                                {avaliacao.proposta ? vertical.labels.documentoFinal : vertical.labels.criarDocumento}
                              </Link>
                            </Button>
                          )}
                          {!mostrarFinanceiro && avaliacao.status === "concluida" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="min-w-[172px] justify-center"
                              asChild
                            >
                              <Link to={diagnosticoPrincipal ? `/diagnostico/${diagnosticoPrincipal.id}` : `/diagnostico/novo?veiculo=${avaliacao.veiculo_id}&avaliacao=${avaliacao.id}`}>
                                {diagnosticoPrincipal?.share_token ? <Send className="mr-2 h-4 w-4" /> : <Stethoscope className="mr-2 h-4 w-4" />}
                                {diagnosticoPrincipal ? "Abrir diagnóstico" : "Criar diagnóstico"}
                              </Link>
                            </Button>
                          )}
                          {!mostrarFinanceiro && atendimento.status !== "entrada_criada" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="min-w-[172px] justify-center text-muted-foreground"
                              asChild
                            >
                              <Link to={`/avaliacao/${avaliacao.id}`}>
                                <FileText className="mr-2 h-4 w-4" />
                                Ver entrada
                              </Link>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}

export default Avaliacoes
